import $ from 'jquery';

import 'etc/designs/base/clientlibs/shared/libraries/SCS.utils.js';

import { Module } from 'lib/vendor/terrific/Module';
import { Hook } from 'lib/vendor/terrific/Sandbox';

import { hashDeepLink } from 'lib/util/url';

import modal from 'lib/generic/SDXModalDialog';
import { flavourSizeFromSiebelId, parseLegoContainerDeeplink } from 'lib/generic/api/map';
import { isMobilePage } from 'lib/util/helpers';

export const DISABLED_VALUES = {
	'mobile-disable': 'N_LEGO No Mobile',
	'tv-disable': 'LEGO No TV',
	'voip-disable': 'Lego No VoIP',
};

export const SKU_DISABLED_VALUES = {
	tv: 'LEGO_no_TV',
	voip: 'Lego_no_VoIP',
};

export const SLIDERS_FLAVOR_MAPPING = {
	'Internet Flavour': 'internet',
	'Tv Flavour': 'tv',
	'Voip Flavour': 'voip',
};

export const MOBILE_ABO_DEVICE_TOGGLE = 'mobileSubscriptionWithDevice';

export type Prices = Record<'bundleBasePrice' | 'homePrice' | 'mobilePrice' | 'totalPrice', number>;

export type ServiceId = 'internet' | 'tv' | 'voip' | 'mobile';


export interface LegoContainerConfig {
	// The flavour siebelId
	slideId : string;
	// The service name [mobile | internet | voip | tv | service]
	sliderId : ServiceId;
	cost : number;
}

export type ConfigState = Omit<LegoContainerConfig, 'cost'>;

export interface CurrentSelection {
	mobile? : LegoContainerConfig;
	internet? : LegoContainerConfig;
	tv? : LegoContainerConfig;
	voip? : LegoContainerConfig;
	service? : LegoContainerConfig;
}


export interface LegoContainerData {
	config : LegoContainerConfig[];
	options : Record<string, string>;
}

export interface Flavour {
	siebelId : string;
	priceDifference : number;
}

export interface FlavourMobile extends Flavour {
	// Mobile only
	priceWithDevice : number;
	price : number;
	siebelName : string;
	summaryLabel : string;
}

export interface Service<T extends Flavour = Flavour> {
	flavours : T[];
}

export interface ProductCatalog {
	bundleSiebelId : string;
	bundleBasePrice : number;
	u26PromoPriceDifference? : number;
	internet : Service;
	tv : Service;
	voip : Service;
	mobile? : Service<FlavourMobile>;
	pricingBundles? : BundlePrice[];
}

export interface BundleServiceConfiguration {
	productType : string;
	skuCode : string;
}

export interface BundlePromotionConfiguration {
	defaultDuration? : number;
	discountAmmount? : number;
	durationType? : string;
	durationUom? : string;
	productType? : string;
	promoSkuCode? : string;
	promotionCommitmentPeriodEligibility? : string;
	promotionType? : string;
}

export interface BundlePrice {
	price : number;
	configuration : BundleServiceConfiguration[];
	eligiblePromotions : BundlePromotionConfiguration[];
}

export interface TrackingInfo {
	action? : string;
	category? : string;
	conversionCategory? : string;
	conversionDetail? : string;
	conversionClass? : string;
	label? : string;
	location? : string;
	linkName? : string;
	selectedProducts? : CurrentSelection | LegoContainerConfig[];
}

/**
 * iterates over an array or object. The only requirement is that the item has a property "slideId".
 */
function getTrackingIdString(data : CurrentSelection | { slideId : string }[]) {
	const arr = Array.isArray(data) ? data : Object.keys(data).map(key => data[key as keyof CurrentSelection]!);

	return arr
		.map(item => item.slideId)
		.join('_');
}

/**
 * Separate slider and checkbox ui elements and return an object
 * with two arrays, one for each type.
 */
function separateSlidesAndCheckboxes(slideConfigurations : LegoContainerConfig[]) {
	const separated = {
		checkboxes: [] as string[],
		slides: [] as string[],
	};

	configLoop: for (const slideConfig of slideConfigurations) {
		for (const key in DISABLED_VALUES) {
			if (DISABLED_VALUES[key as keyof typeof DISABLED_VALUES].indexOf(slideConfig.slideId) > -1) {
				separated.checkboxes.push(slideConfig.slideId);

				continue configLoop; // Continue on each loop
			}
		}

		separated.slides.push(slideConfig.slideId);
	}

	return separated;
}

function keyForValue<V, K extends string | number | symbol = string>(object : Record<K, V>, value : V) : K | undefined {
	for (const key in object) {
		if (object.hasOwnProperty(key) && object[key] === value) {
			return key;
		}
	}

	return undefined;
}

export function toFloat(value : number | string) {
	return parseFloat(value as any as string);
}

/**
 * Lego Configurator module implementation.
 *
 * @author Matthias Inauen
 * @namespace Tc.Module
 * @class LegoContainer
 * @extends Tc.Module
 */
export default class LegoContainer extends Module {
	public initComplete = false;
	public dataLoadAttempts = 0;
	public serviceUrl! : string;
	public legacyCatalogApi = true;
	public segment! : string;
	public u26rebate = false;
	public config? : ProductCatalog;
	public options : Record<string, string> = {};
	public currentSelection : CurrentSelection = {};
	public i18n : Record<string, string> = {};

	public isEditMode = false;

	public mobileConfigData : FlavourMobile[] | null = null;
	public mobileOptional = false;

	public configString : string | null = null;
	public deepLinkConf : LegoContainerData | null = null;

	public ui : Record<string, JQuery<any>> = {};

	public slidersInitialized = 0;
	public disabledSliderState : Partial<Record<ServiceId, string>> = {};

	public on(callback : Hook) {
		this.initComponent();
		this.cacheUi();
		this.initUi();

		this.updateSummaryPosition();
		this.addEventListeners();

		callback();
	}

	public after() {
		// Start module.
		this.loadData();
	}

	public initComponent() {
		this.serviceUrl = this.$ctx.data('serviceurl');
		this.legacyCatalogApi = this.$ctx.data('service-legacy-catalog-api');
		if (!this.legacyCatalogApi) {
			DISABLED_VALUES['tv-disable'] = SKU_DISABLED_VALUES.tv;
			DISABLED_VALUES['voip-disable'] = SKU_DISABLED_VALUES.voip;
		}
		this.segment = this.$ctx.data('segment');
		this.u26rebate = (this.$ctx.data('u26-rebate') && this.segment === 'res');
		this.options[MOBILE_ABO_DEVICE_TOGGLE] = 'false'; // This could be made more flexible, but at the moment this is a fixed default.

		this.i18n.selected = this.$ctx.data('i18n-selected');
		this.i18n.monthShort = this.$ctx.data('i18n-month-abbrev');

		this.isEditMode = this.$ctx.hasClass('isEditMode');
		this.mobileConfigData = this.$ctx.data('mobileConfig') || null;
		this.sandbox.subscribe('LEGO_CONTAINER', this);
		this.mobileOptional = this.$ctx.data('mobile-optional') && !this.isEditMode;

		this.configString = hashDeepLink.get('lpIds') || this.readFromStorage();
		this.deepLinkConf = this.deserializeDeeplink(this.configString);
	}

	public cacheUi() {
		this.ui.summaryBar = this.$ctx.find('.summaryBar');
		this.ui.servicesUl = this.ui.summaryBar.find('.services');
		this.ui.services = this.ui.servicesUl.find('li');
		this.ui.costList = this.ui.summaryBar.find('.cost-list');
		this.ui.benefit = this.ui.summaryBar.find('.benefit');
		this.ui.bundlePrice = this.ui.costList.find('.bundle-price .price span');
		this.ui.mobilePrice = this.ui.costList.find('.mobile-price .price span');
		this.ui.mobilePriceDetails = this.ui.costList.find('li:not(.bundle-price)');
		this.ui.actionLink = this.ui.summaryBar.find('.actions .lego-details-link');
		this.ui.orderButton = this.$ctx.find('.modLegoOrderButton .lego-order-button');
		this.ui.total = this.ui.summaryBar.find('.total span');
		this.ui.collapseButton = this.ui.summaryBar.find('.expand-collapse');
		if (!this.mobileConfigData && isMobilePage()) {
			// The prices are hidden in 3P mode and the services list is hidden in mobile view ⇒ there is nothing to toggle
			this.ui.collapseButton.hide();
		}
		this.ui.document = $(document);
		this.ui.window = $(window);
		this.ui.footer = $('.scs-pagefooter');
		this.ui.u26Rebate = this.ui.summaryBar.find('.u26Rebate');
		this.ui.u26benefit = this.ui.summaryBar.find('.u26benefit');
		this.ui.bundleLinks = this.$ctx.find('.legoBundle');

		if (this.mobileOptional) {
			this.ui.orderButtonWithoutMobile = this.$ctx.find('.service-availability-without-mobile');
			this.ui.orderButtonWithMobile = this.$ctx.find('.service-availability-with-mobile');
		}

		const checkBoxSelector = [];
		for (const key in DISABLED_VALUES) {
			checkBoxSelector.push(`input[name="${key}"]`);
		}

		this.ui.checkboxes = this.$ctx.find(checkBoxSelector.join(','));
	}

	public initUi() {
		if (this.mobileConfigData) {
			this.ui.costList.removeClass('hidden');
		} else {
			this.ui.u26Rebate.addClass('inOneHome');
			this.ui.benefit.addClass('hidden');
		}
	}

	public addEventListeners() {
		this.ui.actionLink.on('click', this.handleActionLink.bind(this));
		this.ui.collapseButton.on('tap', this.handleToggleCollapse.bind(this));
		this.ui.orderButton.on('click', this.handleOrder.bind(this));

		if (!this.isEditMode) {
			// Call it once initially
			this.handleScroll();
			// Then call it on every scroll.
			this.ui.document.on('scroll', this.handleScroll.bind(this));
		}
	}

	/**
	 * Calculate the total price, bundle base price, mobile price, home price
	 * @param data - Array
	 * @return Object
	 */
	public calculateTotalPrice(data : ConfigState[] | CurrentSelection = []) {
		if (this.legacyCatalogApi) {
			return this.calculateTotalPriceLegacy(data);
		}
		$.each(data, (_i, item : LegoContainerConfig) => {
			this.currentSelection[item.sliderId] = {
				cost: 0,
				slideId: item.slideId,
				sliderId: item.sliderId,
			};
		});
		const bundleBasePrice = toFloat(this.config?.pricingBundles?.find(item => {
			return item.configuration.every(config => config.skuCode === this.getSkuCodeForFlavour(config));
		})?.price ?? NaN);

		const prices : Prices = {
			bundleBasePrice,
			homePrice: 0,
			mobilePrice: 0,
			totalPrice : bundleBasePrice,
		};

		return prices;
	}

	public calculateTotalPriceLegacy(data : ConfigState[] | CurrentSelection = []) {
		let bundleBasePrice = toFloat(this.config?.bundleBasePrice ?? NaN);
		if (this.u26rebate) {
			bundleBasePrice -= Math.abs(toFloat(this.config?.u26PromoPriceDifference ?? NaN));
		}
		const prices : Prices = {
			bundleBasePrice,
			homePrice: 0,
			mobilePrice: 0,
			totalPrice: bundleBasePrice,
		};

		$.each(data, (_i, item : LegoContainerConfig) => {
			this.currentSelection[item.sliderId] = {
				cost: this.getFlavourPrice(item.sliderId, item.slideId),
				slideId: item.slideId,
				sliderId: item.sliderId,
			};
		});

		for (const i in this.currentSelection) {
			const slider = this.currentSelection[i as keyof CurrentSelection]!;
			if (slider.sliderId === 'mobile') {
				prices.mobilePrice = slider.cost;
			}
			prices.totalPrice += slider.cost;
		}

		prices.homePrice = prices.totalPrice - prices.mobilePrice;

		return prices;
	}

	public getSkuCodeForFlavour(item : BundleServiceConfiguration) {
		const type = SLIDERS_FLAVOR_MAPPING[item.productType as keyof typeof SLIDERS_FLAVOR_MAPPING];
		const slider = this.currentSelection[type as keyof CurrentSelection];
		if (slider === undefined) {
			return SKU_DISABLED_VALUES[type as keyof typeof SKU_DISABLED_VALUES];
		}

		return slider.slideId;
	}
	/**
	 * - Separate product configuration from options: split('--')
	 * - product config and options are separated by __ (double underscore)
	 * - key/value pairs are separated by - (minus)
	 */
	public deserializeDeeplink(deepLink? : string | null) {
		return parseLegoContainerDeeplink(
			decodeURIComponent(deepLink || ''),
			this.getFlavourPrice.bind(this),
		);
	}

	public getFlavourPrice(serviceId : ServiceId, flavourId : string) {
		if (!this.config) {
			// Wait until config data is loaded via ajax
			return 0;
		}

		if (!(serviceId in this.config)) {
			this.log.warn('Unknown LegoContainer Service ID:', serviceId, this.config);

			return 0;
		}

		const service = this.config[serviceId];

		for (const flavour of service?.flavours ?? []) {
			if (flavour.siebelId && flavour.siebelId === flavourId) {
				return toFloat(flavour.priceDifference);
			}
		}

		this.log.warn('Unknown LegoContainer Flavour ID:', flavourId, service?.flavours);

		return 0;
	}

	public getLocationString() {
		let location = 'inone_';

		location += (this.segment === 'res') ? 'home' : 'pro';
		location += '_configurator_';
		location += (this.mobileConfigData) ? 'mobile' : 'no-mobile';

		return location;
	}

	/**
	 * Trasnform the current selection into a data set with a different form.
	 */
	public getUpdateDataFromCurrentSelection() {
		return (Object.keys(this.currentSelection) as ServiceId[])
			.map(sliderId => {
				const value = this.currentSelection[sliderId]!;

				return {sliderId, slideId: value.slideId, cost: value.cost};
			});
	}

	public loadData() {
		// If the config object is already set, don't load again.
		if (!$.isEmptyObject(this.config)) {
			return;
		}
		// Try to load 3 times if not success
		$.ajax({url: this.serviceUrl, dataType: 'json', headers: {'X-OCE-CLIENT': 'aem-portal'}})
			.done(data => {
				if (data && !$.isEmptyObject(data)) {
					if (this.legacyCatalogApi) {
						this.config = this.segment === 'res' ? data.productCatalogRES : data.productCatalogSME;
					} else {
						this.config = data;
					}
					// Merge mobile data into config data if exists
					if (this.mobileConfigData) {
						this.config!.mobile = {
							flavours: this.mobileConfigData,
						};
					}

					this.handleDataSuccess();
				}
			})
			.fail(() => {
				if (this.dataLoadAttempts < 3) {
					this.dataLoadAttempts += 1;
					setTimeout(this.loadData.bind(this), 500);
				} else {
					this.handleDataFail();
				}
			});
	}

	public handleActionLink(event : JQueryMouseEventObject) {
		const $link = $(event.currentTarget);
		const href = $link.prop('href');
		$link.prop('href', `${href}#lpIds=${this.serializeDeeplink()}`);
	}

	public handleBundleLink(event : JQueryMouseEventObject) {
		event.preventDefault();
		event.stopImmediatePropagation();

		const link = $(event.currentTarget);
		const deepLinks = this.deserializeDeeplink(link.prop('hash').substr(7));

		if (deepLinks && deepLinks.config.length) {
			this.preselectSliderPositions(deepLinks.config);
			const trackingInfo = {
				action: 'select',
				category: 'preselection',
				label: getTrackingIdString(deepLinks.config),
				location: this.getLocationString(),
				selectedProducts: deepLinks.config,
			};

			this.track(trackingInfo);
		} else {
			this.log('handleBundleLink > POSSIBLE CONTENT ISSUE: Url malformed!');
		}
	}

	public handleDataSuccess() {
		if (this.deepLinkConf && this.deepLinkConf.config.length) {
			this.setInitialOptions(this.deepLinkConf.options);
			this.initComplete = true;
			this.updateSummaryUi(this.deepLinkConf.config);
		}
	}

	public handleDataFail() {
		if (this.$ctx.data('map-load-error') && !this.isEditMode) {
			modal.show({
				buttons: [{
					primary: true,
					title: 'Ok',
					type: 'close',
				}],
				displayMode: 'prompt',
				text: this.$ctx.data('map-load-error'),
				title: 'Error',
			});

		}
	}

	public handleOrder() {
		const trackingInfo = {
			conversionCategory: 'offer',
			conversionClass: 'subscription',
			conversionDetail: 'selected',
			product: getTrackingIdString(this.currentSelection),
			selectedProducts: this.currentSelection,
		};

		this.track(trackingInfo);
	}

	public handleScroll() {
		const footerHeight = this.ui.footer.height()!;
		const footerVisible = ((this.ui.document.height()! - this.ui.document.scrollTop()! - this.ui.window.height()!) < footerHeight);
		this.ui.summaryBar.toggleClass('sticky', !footerVisible);

		this.ui.summaryBar.css('bottom', (footerVisible) ? footerHeight : 0);
	}

	public handleToggleCollapse(event : JQueryMouseEventObject) {
		const $icon = this.ui.collapseButton.find('.icon');

		event.preventDefault();
		event.stopImmediatePropagation();

		if ($icon.is('.icon-016-chevron-up')) {
			$icon.removeClass('icon-016-chevron-up').addClass('icon-013-chevron-down');
			this.ui.summaryBar.removeClass('collapsed');
			this.ui.servicesUl.removeClass('hidden');
			this.ui.actionLink.removeClass('hidden');
			if (!this.mobileConfigData) {
				return;
			}
			this.ui.costList.removeClass('hidden');
		} else {
			$icon.removeClass('icon-013-chevron-down').addClass('icon-016-chevron-up');
			this.ui.summaryBar.addClass('collapsed');
			this.ui.costList.addClass('hidden');
			this.ui.actionLink.addClass('hidden');
			this.ui.servicesUl.addClass('hidden');
		}

		this.updateSummaryPosition();
	}

	public isBundleConfiguration() {
		this.ui.bundleLinks.each((_index, link) => {
			const linkServiceIds = this.deserializeDeeplink(link.hash.substring(7));
			$(link)
				.find('.mod.modCard')
				.toggleClass('active', this.isCurrentConfiguration(linkServiceIds?.config ?? []));
		});
	}

	/**
	 * Comparing the link configuration to the current Configuration
	 */
	public isCurrentConfiguration(linkConfiguration : LegoContainerConfig[]) : boolean {
		for (const config of linkConfiguration) {
			const service = this.currentSelection[config.sliderId];

			if (!service || service.slideId !== config.slideId) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Preselect a slider element. Better said, send a slide id to sliders and they will
	 * activate the element if they encounter a match.
	 *
	 * @param slideConfigurations - Array of slide Ids
	 */
	public preselectSliderPositions(slideConfigurations : LegoContainerConfig[]) {
		const slidesAndCheckboxes = separateSlidesAndCheckboxes(slideConfigurations);

		this.toggleCheckboxes(slidesAndCheckboxes.checkboxes);

		if (slidesAndCheckboxes.slides.length) {
			// Send to all sliders to position themselves in the right configuration.
			this.fire('sliderGotoSlide', {slideId: slidesAndCheckboxes.slides}, ['SLIDER']);
		}
	}

	public persistToStorage(dataString : string) {
		try {
			window.sessionStorage.setItem('legoSessionConfig', dataString);
		} catch (e) {
			this.log('Can not persist data to session storage', e);
		}
	}

	public readFromStorage() {
		try {
			return window.sessionStorage.getItem('legoSessionConfig');
		} catch (e) {
			this.log('Can not read from session storage', e);
		}

		return null;
	}

	/**
	 * Pattern for serialization:
	 * - Each serviceId/siebelId pair is combined by a - (minus)
	 * - All pairs are combined by __ (double underscores)
	 * - Options are key/value pairs combined with a - (minus)
	 * - Multiple options can be added by combining with a __ (double underscore)
	 * - Options are appended at the end with a -- (double minus)
	 *
	 * i.e. serviceId_siebelId__serviceId2_siebelId2--option1key=option1value
	 */
	public serializeDeeplink(rawUrl = false) {
		const serialized = Object.keys(this.currentSelection)
			.map(key => {
				const value = this.currentSelection[key as keyof CurrentSelection]!;

				return `${key}-${value.slideId}`;
			});

		const options = [
			// Adding mobile subscription option: with or without device option
			`${MOBILE_ABO_DEVICE_TOGGLE}-${this.options[MOBILE_ABO_DEVICE_TOGGLE]}`,
		];

		const deeplink = `${serialized.join('__')}--${options.join('__')}`;

		return rawUrl ? deeplink : encodeURIComponent(deeplink);
	}

	/**
	 * Setting the initial state of options
	 * (i.e. mobile subscription with or without device)
	 */
	public setInitialOptions(options : Record<string, string>) {
		if (MOBILE_ABO_DEVICE_TOGGLE in options) {
			this.updateMobileDeviceOption(options[MOBILE_ABO_DEVICE_TOGGLE]);
		}
	}

	/**
	 * Toggling the state of a ui checkbox.
	 *
	 * @param checkboxIds - Array of checkbox ids to toggle
	 */
	public toggleCheckboxes(checkboxIds : string[]) {
		const mod = this;

		mod.ui.checkboxes.prop('checked', false).trigger('change');

		// Check if any of the checkboxes are part of the slider configuration:
		for (const checkboxId of checkboxIds) {
			const checkboxKey = keyForValue(DISABLED_VALUES, checkboxId);
			if (checkboxKey) {
				mod.ui.checkboxes.filter(`[name="${checkboxKey}"]`).prop('checked', true).trigger('change');
			}
		}
	}

	public updateMobileDeviceOption(newValue : string) {
		this.options[MOBILE_ABO_DEVICE_TOGGLE] = newValue;
		this.toggleMobilePrices(newValue);
	}

	public toggleMobilePrices(usePriceWithDevice : string) {
		if (!this.config?.mobile?.flavours) {
			return;
		}

		for (const flavour of this.config.mobile.flavours) {
			if (usePriceWithDevice === 'true') {
				flavour.priceDifference = toFloat(flavour.priceWithDevice ?? 0);
			} else {
				flavour.priceDifference = toFloat(flavour.price ?? 0);
			}
		}
	}

	public toggleOrderButton() {
		const mobileDisabled = (this.currentSelection.mobile && this.currentSelection.mobile.slideId === DISABLED_VALUES['mobile-disable']);

		this.ui.orderButtonWithoutMobile.toggleClass('hidden', mobileDisabled);
		this.ui.orderButtonWithMobile.toggleClass('hidden', !mobileDisabled);
	}

	public track(data : TrackingInfo) {
		const interaction = new SCS.portal.datalayer.Interaction();
		SCS.portal.datalayer.resetProductList();

		if (data.conversionCategory && data.conversionDetail) {
			interaction.withConversion(data.conversionCategory, data.conversionDetail, data.conversionClass);
		}

		if (data.selectedProducts) {
			SCS.portal.datalayer.addInOneProduct(this.config!.bundleSiebelId, this.config!.bundleBasePrice, 'bundle');

			$.each((data.selectedProducts as any), function(_index, item : LegoContainerConfig) {
				SCS.portal.datalayer.addInOneProduct(item.slideId, item.cost, 'flavour');
			});
		}

		if (data.action) {
			interaction.withEvent(data.action, data.label, data.location, data.category, data.linkName);
		}

		interaction.track();
	}

	/**
	 * The summary bar has to be below the content.
	 * Hence the lego configurator gets bottom padding in the height of the summaryBar.
	 */
	public updateSummaryPosition() {
		this.$ctx.css('padding-bottom', this.ui.summaryBar.outerHeight(true)!);
	}

	public updateSummaryUi(data : LegoContainerConfig[] | CurrentSelection) {
		const mod = this;
		const prices = mod.calculateTotalPrice(data);

		mod.ui.mobilePriceDetails.removeClass('disabled');
		mod.ui.u26benefit.removeClass('disabled');
		mod.ui.total.text(SCS.utils.formatPriceWithDash(prices.totalPrice));
		mod.ui.services.addClass('hidden');

		$.each(this.currentSelection, function(key, value) {
			if (!value) {
				return;
			}

			const $service = mod.ui.services.filter('.' + String(key));
			const isServiceDisabled = Object.keys(DISABLED_VALUES)
				.map(k => DISABLED_VALUES[k as keyof typeof DISABLED_VALUES])
				.includes(value.slideId);

			$service.toggleClass('hidden', isServiceDisabled);

			if (!isServiceDisabled) {
				let flavourSize;
				if (key === 'mobile') {
					const siebelMapping = $.map(mod.mobileConfigData!, function(val) {
						return val.siebelId === value.slideId ? [val.siebelName, val.summaryLabel || 'inOne'] : null;
					});
					flavourSize = flavourSizeFromSiebelId(siebelMapping[0]);
					mod.ui.costList.find('.mobile-price .abo-type').text(siebelMapping[1]);
				} else {
					flavourSize = flavourSizeFromSiebelId(value.slideId);
				}

				$service.find('span').not('.serviceName, .swisscomIconFont').removeClass().text(flavourSize).addClass(flavourSize);
			} else if (value.slideId === DISABLED_VALUES['mobile-disable']) {
				mod.ui.mobilePriceDetails.addClass('disabled');
				mod.ui.u26benefit.addClass('disabled');
			}
		});

		if (mod.mobileConfigData) {
			mod.ui.mobilePrice.text(SCS.utils.formatPriceWithDash(prices.mobilePrice));
			mod.ui.bundlePrice.text(SCS.utils.formatPriceWithDash(prices.homePrice));
		}

		if (mod.mobileOptional) {
			this.toggleOrderButton();
		}

		mod.updateSummaryPosition();

		// Only after initialization is complete we should rewrite the hash and local storage.
		if (mod.initComplete) {
			// Get the deeplink unencoded as `SCS.HashDeepLink.set()` will encode the url also...
			const deeplink = mod.serializeDeeplink(true);
			// ... but encode it for local storage, because `deserializeDeepLink` will expect an encoded string
			mod.persistToStorage(encodeURIComponent(deeplink));
			hashDeepLink.set('lpIds', deeplink, ',', false);
		}
	}

	/**
	 * Informs the Lego Order Button about selection changes.
	 * Sends the selected products IDs, current prices, and all available Internet flavours.
	 */
	public broadcastConfigurationChanged(currentSelection : CurrentSelection, prices : Prices) {
		// Internet should always be set, but it is possible that this
		// method is called before the internet service is initialized.
		if (!this.config?.internet) {
			return;
		}

		const productIds = this.config.internet.flavours;
		let deeplinkHashParameters = this.serializeDeeplink();

		if (this.currentSelection.mobile && this.currentSelection.mobile.slideId) {
			// The only reason for the subscriptionId parameter is for the 4p order form and was implemented as a quickfix
			// the 4p form hosts a hidden field with the name of `subscriptionId` that will pickup this value automatically.
			// this param is NOT needed for the lego container.
			deeplinkHashParameters += '&subscriptionId=' + encodeURIComponent(this.currentSelection.mobile.slideId);
		}

		this.log('broadcastConfigurationChanged', currentSelection, deeplinkHashParameters, prices, productIds);
		this.fire('newLegoConfiguration', {
			currentSelection,
			deeplinkHashParameters,
			prices,
			productIds,
		}, ['LEGO_CONTAINER']);
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/lego/Tc.Module.LegoContainer.ts